import React, { useCallback, useEffect, useMemo, useState } from "react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Tabs from "src/components/ui/Tabs";
import Card from "src/components/common/card/Card";
import TableV2 from "src/components/ui/TableV2";
import BarChart from "src/components/ui/chart/BarChart";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import Dropdown from "src/components/ui/Dropdown";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import categoryApis from "src/api/category";
import productApis from "src/api/product";
import tenantUserApis from "src/api/tenantUser";
import { Category } from "src/types/category.types";
import { Product } from "src/types/product.types";
import { TenantUser } from "src/types/tenantUser.types";
import { toast } from "react-toastify";
import Spinner from "src/components/ui/Spinner";
import Pagination from "src/components/ui/Pagination";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState<"7" | "30" | "90" | "custom">(
    "30"
  );
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [appliedRange, setAppliedRange] = useState<
    | { type: "preset"; days: number }
    | { type: "custom"; startDate: string; endDate: string }
  >({ type: "preset", days: 30 });
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchRequested, setFetchRequested] = useState(false);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const dispatch = useAppDispatch();
  const {
    tenantDashboardSummary,
    tenantStockMovement,
    tenantSalesOverview,
    tenantPurchaseOverview,
    tenantCategorySales,
    tenantTopProducts,
    tenantRecentSales,
    tenantRecentPurchases,
    tenantLowStockAlerts,
  } = useAppSelector((state) => state.dashboard);

  const permissions = useMemo(
    () => user?.roleDetails?.permissions || user?.permissions || {},
    [user?.roleDetails?.permissions, user?.permissions]
  );
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const hasPermission = useCallback(
    (module?: string, action: string = "view") => {
      if (!module) return true;
      const modulePerms = permissions?.[module] || [];
      return modulePerms.includes(action);
    },
    [permissions]
  );

  const permissionEntries = Object.keys(permissions || {});
  const shouldFilter = permissionEntries.length > 0 && !isTenantAdmin;
  const canSeeModule = (module: string) => {
    if (!shouldFilter) return true;
    return (permissions?.[module] || []).length > 0;
  };

  const canViewReports =
    isTenantAdmin || permissionEntries.length === 0 || hasPermission("reports");

  const tabs = useMemo(() => {
    if (!canViewReports) return [];
    const list = [
      { label: "Overview", value: "overview" },
      { label: "Sales", value: "sales", module: "sales" },
      { label: "Inventory", value: "inventory", module: "inventory" },
      { label: "Purchases", value: "purchases", module: "purchases" },
      { label: "Products", value: "products", module: "products" },
    ];

    if (!shouldFilter) {
      return list.map(({ label, value }) => ({ label, value }));
    }

    return list
      .filter((tab) => (tab.module ? hasPermission(tab.module, "view") : true))
      .map(({ label, value }) => ({ label, value }));
  }, [canViewReports, hasPermission, shouldFilter]);

  useEffect(() => {
    if (tabs.length === 0) return;
    const tabValues = tabs.map((tab) => tab.value);
    if (!tabValues.includes(activeTab)) {
      setActiveTab(tabValues[0]);
    }
  }, [tabs, activeTab]);

  const isCustomRangeValid =
    !!customStartDate && !!customEndDate && customStartDate <= customEndDate;

  const showProducts = canSeeModule("products");
  const showInventory = canSeeModule("inventory");
  const showSales = canSeeModule("sales");
  const showPurchases = canSeeModule("purchases");
  const showStockMovement = showInventory || showProducts;
  const showLowStock = showInventory || showProducts;
  const needsSummary =
    showProducts || showInventory || showSales || showPurchases;
  const canReorder =
    isTenantAdmin || (permissions?.purchases || []).includes("create");

  useEffect(() => {
    if (!canViewReports) return;
    const loadFilters = async () => {
      try {
        const [categoryRes, userRes] = await Promise.all([
          categoryApis.getCategoryList({ page: 1, limit: 200 }),
          tenantUserApis.getTenantUserList({ page: 1, limit: 200 }),
        ]);

        setCategories(categoryRes?.data?.data || categoryRes?.data || []);
        setUsers(userRes?.data?.data || userRes?.data || []);
      } catch (error) {
        toast.error("Failed to load report filters");
      }
    };

    loadFilters();
  }, [canViewReports]);

  useEffect(() => {
    if (!canViewReports) return;
    const loadProducts = async () => {
      try {
        const params: {
          page?: number;
          limit: number;
          categoryId?: string;
        } = {
          page: 1,
          limit: 500,
        };
        if (selectedCategoryId) {
          params.categoryId = selectedCategoryId;
        }
        const productRes = await productApis.getProductList(params);
        setProducts(productRes?.data?.data || productRes?.data || []);
      } catch (error) {
        toast.error("Failed to load products");
      }
    };

    loadProducts();
  }, [canViewReports, selectedCategoryId]);

  const reportParams = useMemo(() => {
    const rangeParams =
      appliedRange.type === "custom"
        ? {
            startDate: appliedRange.startDate,
            endDate: appliedRange.endDate,
          }
        : { days: appliedRange.days };

    return {
      ...rangeParams,
      ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
      ...(selectedProductId ? { productId: selectedProductId } : {}),
      ...(selectedUserId ? { userId: selectedUserId } : {}),
    };
  }, [
    appliedRange,
    selectedCategoryId,
    selectedProductId,
    selectedUserId,
  ]);

  const fetchReports = () => {
    if (!canViewReports || !reportParams) return;
    if (needsSummary) {
      dispatch(dashboardActions.getTenantDashboardSummaryRequest(reportParams));
    }
    if (showStockMovement) {
      dispatch(
        dashboardActions.getTenantStockMovementRequest({
          ...reportParams,
        })
      );
    }
    if (showSales) {
      dispatch(
        dashboardActions.getTenantSalesOverviewRequest({
          ...reportParams,
        })
      );
      dispatch(
        dashboardActions.getTenantCategorySalesRequest({
          ...reportParams,
          limit: 8,
        })
      );
      dispatch(
        dashboardActions.getTenantTopProductsRequest({
          ...reportParams,
          limit: 8,
        })
      );
      dispatch(
        dashboardActions.getTenantRecentSalesRequest({
          ...reportParams,
          limit: 10,
        })
      );
    }
    if (showPurchases) {
      dispatch(
        dashboardActions.getTenantPurchaseOverviewRequest({
          ...reportParams,
        })
      );
      dispatch(
        dashboardActions.getTenantRecentPurchasesRequest({
          ...reportParams,
          limit: 10,
        })
      );
    }
    if (showLowStock) {
      dispatch(
        dashboardActions.getTenantLowStockAlertsRequest({
          ...reportParams,
          limit: 10,
        })
      );
    }
  };

  const summary = tenantDashboardSummary.data;
  const stockSeries = useMemo(
    () => tenantStockMovement.data?.data || [],
    [tenantStockMovement.data]
  );
  const salesSeries = useMemo(
    () => tenantSalesOverview.data?.data || [],
    [tenantSalesOverview.data]
  );
  const purchaseSeries = useMemo(
    () => tenantPurchaseOverview.data?.data || [],
    [tenantPurchaseOverview.data]
  );
  const categorySales = useMemo(
    () => tenantCategorySales.data?.data || [],
    [tenantCategorySales.data]
  );
  const topProducts = useMemo(
    () => tenantTopProducts.data?.data || [],
    [tenantTopProducts.data]
  );
  const recentSalesRows = useMemo(
    () => tenantRecentSales.data?.data || [],
    [tenantRecentSales.data]
  );
  const recentPurchaseRows = useMemo(
    () => tenantRecentPurchases.data?.data || [],
    [tenantRecentPurchases.data]
  );
  const lowStockRows = showLowStock
    ? tenantLowStockAlerts.data?.products || []
    : [];

  const totalSalesAmount = useMemo(
    () => salesSeries.reduce((sum, point) => sum + (point.totalSales || 0), 0),
    [salesSeries]
  );
  const totalPurchaseAmount = useMemo(
    () =>
      purchaseSeries.reduce(
        (sum, point) => sum + (point.totalPurchases || 0),
        0
      ),
    [purchaseSeries]
  );
  const overviewCards = useMemo(() => {
    const cards: { label: string; value: string | number }[] = [];
    if (showSales) {
      cards.push(
        {
          label: "Sales Amount",
          value: currencyFormatter(totalSalesAmount),
        },
        {
          label: "Sales Orders",
          value: summary?.totalSalesOrders ?? 0,
        }
      );
    }
    if (showPurchases) {
      cards.push(
        {
          label: "Purchase Amount",
          value: currencyFormatter(totalPurchaseAmount),
        },
        {
          label: "Purchase Orders",
          value: summary?.totalPurchaseOrders ?? 0,
        }
      );
    }
    if (showInventory) {
      cards.push(
        {
          label: "Low Stock Items",
          value: summary?.lowStockCount ?? 0,
        },
        {
          label: "Stock Value",
          value: currencyFormatter(summary?.stockValue ?? 0),
        }
      );
    }
    return cards;
  }, [
    showSales,
    showPurchases,
    showInventory,
    summary,
    totalSalesAmount,
    totalPurchaseAmount,
  ]);
  const appliedRangeLabel = useMemo(() => {
    if (appliedRange.type === "custom") {
      return `Custom: ${appliedRange.startDate} → ${appliedRange.endDate}`;
    }
    return `Last ${appliedRange.days} days`;
  }, [appliedRange]);
  const isReportLoading =
    tenantDashboardSummary.loading ||
    tenantStockMovement.loading ||
    tenantSalesOverview.loading ||
    tenantPurchaseOverview.loading ||
    tenantCategorySales.loading ||
    tenantTopProducts.loading ||
    tenantRecentSales.loading ||
    tenantRecentPurchases.loading ||
    tenantLowStockAlerts.loading;
  const [salesPage, setSalesPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const [lowStockPage, setLowStockPage] = useState(1);
  const tablePageSize = 5;
  const totalSalesPages = Math.max(
    Math.ceil(recentSalesRows.length / tablePageSize),
    1
  );
  const totalPurchasePages = Math.max(
    Math.ceil(recentPurchaseRows.length / tablePageSize),
    1
  );
  const totalLowStockPages = Math.max(
    Math.ceil(lowStockRows.length / tablePageSize),
    1
  );
  const pagedSalesRows = recentSalesRows.slice(
    (salesPage - 1) * tablePageSize,
    salesPage * tablePageSize
  );
  const pagedPurchaseRows = recentPurchaseRows.slice(
    (purchasePage - 1) * tablePageSize,
    purchasePage * tablePageSize
  );
  const pagedLowStockRows = lowStockRows.slice(
    (lowStockPage - 1) * tablePageSize,
    lowStockPage * tablePageSize
  );

  const lowStockColumns = useMemo(
    () => [
      {
        key: "productId",
        header: "Product ID",
        accessor: ({ row }: { row: any }) => (
          <span className="text-gray-600">{row.productId || "-"}</span>
        ),
      },
      {
        key: "name",
        header: "Product",
        accessor: ({ row }: { row: any }) => (
          <span className="font-medium text-gray-900">{row.name}</span>
        ),
      },
      { key: "stock", header: "Current Stock" },
      { key: "minStock", header: "Reorder Level" },
      {
        key: "action",
        header: "Action",
        accessor: () =>
          canReorder ? (
            <Link
              to="/app/purchase/list"
              className="rounded-md border border-primary-200 px-3 py-1 text-xs font-semibold text-primary-600 hover:border-primary-300"
            >
              Reorder
            </Link>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          ),
      },
    ],
    [canReorder]
  );

  const recentSalesColumns = useMemo(
    () => [
      {
        key: "orderId",
        header: "Order",
        accessor: ({ row }: { row: any }) => (
          <Link to={`/app/sale/${row._id}`} className="text-primary-600">
            {row.orderId}
          </Link>
        ),
      },
      {
        key: "customer",
        header: "Customer",
        accessor: ({ row }: { row: any }) => row.customerId?.name || "-",
      },
      {
        key: "grandTotal",
        header: "Total",
        align: "right" as const,
        accessor: ({ row }: { row: any }) =>
          currencyFormatter(row.grandTotal || 0),
      },
      {
        key: "createdAt",
        header: "Date",
        align: "center" as const,
        accessor: ({ row }: { row: any }) =>
          formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A"),
      },
    ],
    []
  );

  const recentPurchaseColumns = useMemo(
    () => [
      {
        key: "purchaseOrderId",
        header: "Order",
        accessor: ({ row }: { row: any }) => (
          <Link to={`/app/purchase/${row._id}`} className="text-primary-600">
            {row.purchaseOrderId}
          </Link>
        ),
      },
      {
        key: "supplier",
        header: "Supplier",
        accessor: ({ row }: { row: any }) => row.supplierId?.name || "-",
      },
      {
        key: "grandTotal",
        header: "Total",
        align: "right" as const,
        accessor: ({ row }: { row: any }) =>
          currencyFormatter(row.grandTotal || 0),
      },
      {
        key: "createdAt",
        header: "Date",
        align: "center" as const,
        accessor: ({ row }: { row: any }) =>
          formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A"),
      },
    ],
    []
  );

  useEffect(() => {
    setSalesPage(1);
  }, [recentSalesRows.length]);

  useEffect(() => {
    setPurchasePage(1);
  }, [recentPurchaseRows.length]);

  useEffect(() => {
    setLowStockPage(1);
  }, [lowStockRows.length]);

  const handleRangeChange = (value: "7" | "30" | "90" | "custom") => {
    setDateRange(value);
    if (value !== "custom") {
      setAppliedRange({ type: "preset", days: Number(value) });
    }
  };

  const handleApplyCustomRange = () => {
    if (!isCustomRangeValid) return;
    setAppliedRange({
      type: "custom",
      startDate: customStartDate,
      endDate: customEndDate,
    });
  };

  const handleResetAll = () => {
    setSelectedCategoryId("");
    setSelectedProductId("");
    setSelectedUserId("");
    setCustomStartDate("");
    setCustomEndDate("");
    setDateRange("30");
    setAppliedRange({ type: "preset", days: 30 });
    setHasFetched(false);
    setFetchRequested(false);
  };

  useEffect(() => {
    if (selectedCategoryId) {
      setSelectedProductId("");
    }
  }, [selectedCategoryId]);
  const handleFetchReports = () => {
    if (!reportParams) return;
    setFetchRequested(true);
    setHasFetched(false);
    fetchReports();
  };

  useEffect(() => {
    if (!fetchRequested) return;
    if (!isReportLoading) {
      setHasFetched(true);
      setFetchRequested(false);
    }
  }, [fetchRequested, isReportLoading]);

  useEffect(() => {
    if (reportParams) {
      setHasFetched(false);
    }
  }, [reportParams]);

  const handleExportExcel = () => {
    if (!hasFetched) {
      toast.info("Fetch reports before exporting");
      return;
    }

    const workbook = XLSX.utils.book_new();

    const overviewSheet = XLSX.utils.json_to_sheet(
      overviewCards.map((item) => ({
        Metric: item.label,
        Value: item.value,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, overviewSheet, "Overview");

    const salesOverviewSheet = XLSX.utils.json_to_sheet(
      salesSeries.map((row) => ({
        Date: row.date,
        TotalSales: row.totalSales,
      }))
    );
    XLSX.utils.book_append_sheet(
      workbook,
      salesOverviewSheet,
      "Sales Overview"
    );

    const salesCategorySheet = XLSX.utils.json_to_sheet(
      categorySales.map((row) => ({
        Category: row.category,
        TotalSales: row.totalSales,
      }))
    );
    XLSX.utils.book_append_sheet(
      workbook,
      salesCategorySheet,
      "Sales Category"
    );

    const topProductsSheet = XLSX.utils.json_to_sheet(
      topProducts.map((row) => ({
        Product: row.name,
        ProductId: row.productId,
        TotalSales: row.totalSales,
        TotalQuantity: row.totalQuantity,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, topProductsSheet, "Top Products");

    const recentSalesSheet = XLSX.utils.json_to_sheet(
      recentSalesRows.map((row) => ({
        Order: row.orderId || row._id || "-",
        Customer: row.customerId?.name || "-",
        Total: row.grandTotal ?? 0,
        Date: row.createdAt || "",
      }))
    );
    XLSX.utils.book_append_sheet(workbook, recentSalesSheet, "Recent Sales");

    const stockMovementSheet = XLSX.utils.json_to_sheet(
      stockSeries.map((row) => ({
        Date: row.date,
        StockIn: row.stockIn,
        StockOut: row.stockOut,
      }))
    );
    XLSX.utils.book_append_sheet(
      workbook,
      stockMovementSheet,
      "Stock Movement"
    );

    const lowStockSheet = XLSX.utils.json_to_sheet(
      lowStockRows.map((row) => ({
        ProductId: row.productId || "-",
        Product: row.name,
        Stock: row.stock,
        MinStock: row.minStock,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, lowStockSheet, "Low Stock");

    const purchaseOverviewSheet = XLSX.utils.json_to_sheet(
      purchaseSeries.map((row) => ({
        Date: row.date,
        TotalPurchases: row.totalPurchases,
      }))
    );
    XLSX.utils.book_append_sheet(
      workbook,
      purchaseOverviewSheet,
      "Purchase Overview"
    );

    const recentPurchasesSheet = XLSX.utils.json_to_sheet(
      recentPurchaseRows.map((row) => ({
        Order: row.purchaseOrderId || row._id || "-",
        Supplier: row.supplierId?.name || "-",
        Total: row.grandTotal ?? 0,
        Date: row.createdAt || "",
      }))
    );
    XLSX.utils.book_append_sheet(
      workbook,
      recentPurchasesSheet,
      "Recent Purchases"
    );

    XLSX.writeFile(workbook, "reports.xlsx");
  };

  return (
    <DashboardLayout
      meta={{
        title: "Reports | Invyntra",
        description: "Track sales, inventory, and purchase insights.",
        keywords: "reports, analytics, sales, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Reports"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Reports", path: "/app/reports" },
          ]}
        />

        {!canViewReports ? (
          <Card>
            <p className="text-sm text-gray-600">
              You do not have access to view reports. Please contact your admin
              to request access.
            </p>
          </Card>
        ) : tabs.length === 0 ? (
          <Card>
            <p className="text-sm text-gray-600">
              No report modules are available for your role. Please contact your
              admin to request access.
            </p>
          </Card>
        ) : (
          <>
            <Card>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      Date Range
                    </span>
                    {(["7", "30", "90"] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleRangeChange(value)}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          dateRange === value
                            ? "bg-primary-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {value} days
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleRangeChange("custom")}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                        dateRange === "custom"
                          ? "bg-primary-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      Custom
                    </button>
                    <span className="text-xs text-gray-500">
                      {appliedRangeLabel}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={handleResetAll}>
                      Reset All
                    </Button>
                    <Button onClick={handleFetchReports}>Fetch</Button>
                    <Button
                      variant="outline"
                      onClick={handleExportExcel}
                      disabled={!hasFetched}
                    >
                      Export
                    </Button>
                  </div>
                </div>

                {dateRange === "custom" && (
                  <div className="grid grid-cols-1 md:grid-cols-[repeat(2,minmax(0,220px))_auto] gap-3 items-end">
                    <Input
                      label="Start Date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                    <Input
                      label="End Date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                    <Button
                      onClick={handleApplyCustomRange}
                      disabled={!isCustomRangeValid}
                    >
                      Apply
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Dropdown
                      label="Category"
                      value={selectedCategoryId}
                      options={categories.map((category) => ({
                        label:
                          category.name || category.categoryId || "Category",
                        value: category._id || "",
                      }))}
                      onChange={setSelectedCategoryId}
                    />
                    {selectedCategoryId && (
                      <button
                        type="button"
                        onClick={() => setSelectedCategoryId("")}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear category
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Dropdown
                      label="Product"
                      value={selectedProductId}
                      options={products.map((product) => ({
                        label: product.name || product.productId || "Product",
                        value: product._id || "",
                      }))}
                      onChange={setSelectedProductId}
                    />
                    {selectedProductId && (
                      <button
                        type="button"
                        onClick={() => setSelectedProductId("")}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear product
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Dropdown
                      label="User"
                      value={selectedUserId}
                      options={users.map((tenantUser: TenantUser) => ({
                        label: `${
                          `${tenantUser.firstName || ""} ${
                            tenantUser.lastName
                          }` ||
                          "".trim() ||
                          tenantUser.email ||
                          tenantUser._id ||
                          "User"
                        } ${
                          tenantUser?.role ? `(${tenantUser?.role?.name})` : ""
                        }`,
                        value: tenantUser._id || "",
                      }))}
                      onChange={setSelectedUserId}
                    />
                    {selectedUserId && (
                      <button
                        type="button"
                        onClick={() => setSelectedUserId("")}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear user
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {!hasFetched && !isReportLoading ? (
              <Card>
                <div className="py-8 text-center space-y-2">
                  <p className="text-sm font-semibold text-gray-700">
                    No reports loaded yet
                  </p>
                  <p className="text-sm text-gray-500">
                    Please select filters and click “Fetch” to generate the
                    report.
                  </p>
                </div>
              </Card>
            ) : isReportLoading ? (
              <Card>
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                  <Spinner size="md" />
                  <p className="text-md text-gray-600">
                    Loading report data...
                  </p>
                </div>
              </Card>
            ) : (
              <>
                <Tabs
                  tabs={tabs}
                  activeTab={activeTab}
                  onChange={setActiveTab}
                  className="flex flex-wrap gap-2 border-b"
                />
                {activeTab === "overview" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                      {overviewCards.map((item) => (
                        <Card key={item.label}>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="text-2xl font-semibold text-gray-800 mt-2">
                            {item.value}
                          </p>
                        </Card>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {showSales && (
                        <Card>
                          <p className="text-sm text-gray-600 font-medium">
                            Sales Overview
                          </p>
                          {salesSeries.length === 0 ? (
                            <p className="mt-4 text-sm text-gray-500">
                              No sales data for the selected range.
                            </p>
                          ) : (
                            <BarChart
                              data={salesSeries}
                              dataKey="totalSales"
                              xKey="date"
                              hideXAxis
                              hideYAxis
                              barColor="#A5B4FC"
                              barRadius={[8, 8, 8, 8]}
                              tooltipFormatter={(value?: number) =>
                                currencyFormatter(value || 0, "IN", 2)
                              }
                            />
                          )}
                        </Card>
                      )}
                      {showStockMovement && (
                        <Card>
                          <p className="text-sm text-gray-600 font-medium">
                            Stock Movement
                          </p>
                          {stockSeries.length === 0 ? (
                            <p className="mt-4 text-sm text-gray-500">
                              No stock movement data for the selected range.
                            </p>
                          ) : (
                            <div className="mt-4 h-44 w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                  data={stockSeries}
                                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="stockInFill"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="0%"
                                        stopColor="#4ADE80"
                                        stopOpacity={0.5}
                                      />
                                      <stop
                                        offset="100%"
                                        stopColor="#4ADE80"
                                        stopOpacity={0.05}
                                      />
                                    </linearGradient>
                                    <linearGradient
                                      id="stockOutFill"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="0%"
                                        stopColor="#F87171"
                                        stopOpacity={0.4}
                                      />
                                      <stop
                                        offset="100%"
                                        stopColor="#F87171"
                                        stopOpacity={0.05}
                                      />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid stroke="#E5E7EB" vertical={false} />
                                  <XAxis dataKey="date" hide />
                                  <YAxis hide />
                                  <Tooltip
                                    contentStyle={{
                                      borderRadius: "8px",
                                      borderColor: "#E5E7EB",
                                      fontSize: "12px",
                                    }}
                                    labelStyle={{ fontWeight: 600 }}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="stockIn"
                                    stroke="#16A34A"
                                    strokeWidth={2}
                                    fill="url(#stockInFill)"
                                    dot={false}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="stockOut"
                                    stroke="#DC2626"
                                    strokeWidth={2}
                                    fill="url(#stockOutFill)"
                                    dot={false}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                        </Card>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "sales" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Weekly Sales
                      </p>
                      {salesSeries.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No sales data for the selected range.
                        </p>
                      ) : (
                        <BarChart
                          data={salesSeries}
                          dataKey="totalSales"
                          xKey="date"
                          hideXAxis
                          hideYAxis
                          barColor="#A5B4FC"
                          barRadius={[8, 8, 8, 8]}
                          tooltipFormatter={(value?: number) =>
                            currencyFormatter(value || 0, "IN", 2)
                          }
                        />
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Top Products
                      </p>
                      {topProducts.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No product sales for the selected range.
                        </p>
                      ) : (
                        <BarChart
                          data={topProducts}
                          dataKey="totalSales"
                          xKey="name"
                          containerClassName="mt-4 h-52 w-full"
                          hideYAxis
                          barColor="#86EFAC"
                          barRadius={[8, 8, 8, 8]}
                          xAxisProps={{
                            tick: { fontSize: 11 },
                            interval: 0,
                            angle: -20,
                            textAnchor: "end",
                            height: 50,
                          }}
                          tooltipFormatter={(value?: number) =>
                            currencyFormatter(value || 0, "IN", 2)
                          }
                        />
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Sales by Category
                      </p>
                      {categorySales.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No category sales for the selected range.
                        </p>
                      ) : (
                        <BarChart
                          data={categorySales}
                          dataKey="totalSales"
                          xKey="category"
                          containerClassName="mt-4 h-52 w-full"
                          hideYAxis
                          barColor="#FCD34D"
                          barRadius={[8, 8, 8, 8]}
                          xAxisProps={{
                            tick: { fontSize: 11 },
                            interval: 0,
                            angle: -20,
                            textAnchor: "end",
                            height: 50,
                          }}
                          tooltipFormatter={(value?: number) =>
                            currencyFormatter(value || 0, "IN", 2)
                          }
                        />
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Recent Sales
                      </p>
                      <div className="mt-4">
                        {recentSalesRows.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No recent sales found.
                          </p>
                        ) : (
                          <>
                            <TableV2
                              columns={recentSalesColumns}
                              data={pagedSalesRows}
                            />
                            <Pagination
                              currentPage={salesPage}
                              pageLimit={tablePageSize}
                              totalPages={totalSalesPages}
                              totalRecords={recentSalesRows.length}
                              onPageChange={setSalesPage}
                            />
                          </>
                        )}
                      </div>
                    </Card>
                  </div>
                )}

                {activeTab === "inventory" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Stock Movement
                      </p>
                      {stockSeries.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No stock movement data for the selected range.
                        </p>
                      ) : (
                        <div className="mt-4 h-44 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={stockSeries}
                              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient
                                  id="stockInFill"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="0%"
                                    stopColor="#4ADE80"
                                    stopOpacity={0.5}
                                  />
                                  <stop
                                    offset="100%"
                                    stopColor="#4ADE80"
                                    stopOpacity={0.05}
                                  />
                                </linearGradient>
                                <linearGradient
                                  id="stockOutFill"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="0%"
                                    stopColor="#F87171"
                                    stopOpacity={0.4}
                                  />
                                  <stop
                                    offset="100%"
                                    stopColor="#F87171"
                                    stopOpacity={0.05}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid stroke="#E5E7EB" vertical={false} />
                              <XAxis dataKey="date" hide />
                              <YAxis hide />
                              <Tooltip
                                contentStyle={{
                                  borderRadius: "8px",
                                  borderColor: "#E5E7EB",
                                  fontSize: "12px",
                                }}
                                labelStyle={{ fontWeight: 600 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="stockIn"
                                stroke="#16A34A"
                                strokeWidth={2}
                                fill="url(#stockInFill)"
                                dot={false}
                              />
                              <Area
                                type="monotone"
                                dataKey="stockOut"
                                stroke="#DC2626"
                                strokeWidth={2}
                                fill="url(#stockOutFill)"
                                dot={false}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Low Stock Watchlist
                      </p>
                      {lowStockRows.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No low stock items found.
                        </p>
                      ) : (
                        <>
                          <TableV2
                            columns={lowStockColumns}
                            data={pagedLowStockRows}
                          />
                          <Pagination
                            currentPage={lowStockPage}
                            pageLimit={tablePageSize}
                            totalPages={totalLowStockPages}
                            totalRecords={lowStockRows.length}
                            onPageChange={setLowStockPage}
                          />
                        </>
                      )}
                    </Card>
                  </div>
                )}

                {activeTab === "purchases" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Weekly Purchases
                      </p>
                      {purchaseSeries.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No purchase data for the selected range.
                        </p>
                      ) : (
                        <BarChart
                          data={purchaseSeries}
                          dataKey="totalPurchases"
                          xKey="date"
                          hideXAxis
                          hideYAxis
                          barColor="#93C5FD"
                          barRadius={[8, 8, 8, 8]}
                          tooltipFormatter={(value?: number) =>
                            currencyFormatter(value || 0, "IN", 2)
                          }
                        />
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Recent Purchases
                      </p>
                      <div className="mt-4">
                        {recentPurchaseRows.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No recent purchases found.
                          </p>
                        ) : (
                          <>
                            <TableV2
                              columns={recentPurchaseColumns}
                              data={pagedPurchaseRows}
                            />
                            <Pagination
                              currentPage={purchasePage}
                              pageLimit={tablePageSize}
                              totalPages={totalPurchasePages}
                              totalRecords={recentPurchaseRows.length}
                              onPageChange={setPurchasePage}
                            />
                          </>
                        )}
                      </div>
                    </Card>
                  </div>
                )}

                {activeTab === "products" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Top Products
                      </p>
                      {topProducts.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No product sales for the selected range.
                        </p>
                      ) : (
                        <BarChart
                          data={topProducts}
                          dataKey="totalSales"
                          xKey="name"
                          containerClassName="mt-4 h-52 w-full"
                          hideYAxis
                          barColor="#86EFAC"
                          barRadius={[8, 8, 8, 8]}
                          xAxisProps={{
                            tick: { fontSize: 11 },
                            interval: 0,
                            angle: -20,
                            textAnchor: "end",
                            height: 50,
                          }}
                          tooltipFormatter={(value?: number) =>
                            currencyFormatter(value || 0, "IN", 2)
                          }
                        />
                      )}
                    </Card>
                    <Card>
                      <p className="text-sm text-gray-600 font-medium">
                        Low Stock Watchlist
                      </p>
                      {lowStockRows.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500">
                          No low stock items found.
                        </p>
                      ) : (
                        <>
                          <TableV2
                            columns={lowStockColumns}
                            data={pagedLowStockRows}
                          />
                          <Pagination
                            currentPage={lowStockPage}
                            pageLimit={tablePageSize}
                            totalPages={totalLowStockPages}
                            totalRecords={lowStockRows.length}
                            onPageChange={setLowStockPage}
                          />
                        </>
                      )}
                    </Card>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
